import { Injectable, Type } from '@angular/core';
import { Error } from '../models/error-response';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { ErrorComponent } from '../modals/error/error.component';
import { GenericComponent } from '../modals/generic/generic.component';
import { CompilerComponent } from '../modals/compiler/compiler.component';
import { InputTextAreaComponent } from '../modals/input-text-area/input-text-area.component';
import { CloseInvoiceConfirmationComponent } from '../modals/close-invoice-confirmation/close-invoice-confirmation.component';
import { InputNameValuePairComponent } from '../modals/input-name-value-pair/input-name-value-pair.component';
import { FormGroup } from '@angular/forms';
import { AccountContactComponent } from '../modals/account-contact/account-contact.component';
import { AccountContactListComponent } from '../modals/account-contact-list/account-contact-list.component';
import { AccountArrangementSourceListComponent } from '../modals/account-arrangement-source-list/account-arrangement-source-list.component';
import { AccountReligionListComponent } from '../modals/account-religion-list/account-religion-list.component';
import { AccountHospitalAndNursingHomeComponent } from '../modals/account-hospital-and-nursing-home/account-hospital-and-nursing-home.component';
import { AccountHospitalAndNursingHomeListComponent } from '../modals/account-hospital-and-nursing-home-list/account-hospital-and-nursing-home-list.component';
import { InputSignatureComponent } from '../modals/input-signature/input-signature.component';
import { ActivateAccountModalComponent } from 'src/app/pages/register/activate-account-modal/activate-account-modal.component';
import { AccountEventTypeListComponent } from '../modals/account-event-type-list/account-event-type-list.component';
import { AccountCharityListComponent } from '../modals/account-charity-list/account-charity-list.component';
import { AccountFuneralCostListComponent } from '../modals/account-funeral-cost-list/account-funeral-cost-list.component';
import { AccountFaqListComponent } from '../modals/account-faq-list/account-faq-list.component';
import { ContentRegionListComponent } from '../modals/content/content-region-list.component';
import { ContentSuburbWorkspaceComponent } from '../modals/content/suburbs/content-suburb-workspace.component';
import { VenueMapComponent, VenueMapData } from '../modals/venue-map/venue-map.component';
import { ContentCharityListComponent } from '../modals/content/content-charity-list.component';
import { ContentBlogListComponent } from '../modals/content/content-blog-list.component';
import { ContentPageListComponent } from '../modals/content/content-page-list.component';
import { ContentMenuListComponent } from '../modals/content/content-menu-list.component';
import { ContentTemplateCopyListComponent } from '../modals/content/content-template-copy-list.component';
import { MarkdownHelpComponent } from '../modals/markdown-help/markdown-help.component';
import { ContentProfileListComponent } from '../modals/content-profile-list/content-profile-list.component';
import { ContentProfileComponent } from '../modals/content-profile/content-profile.component';
import { ContentSiteServiceListComponent } from '../modals/content/content-site-service-list.component';
import { BdmNswAuthenticateComponent } from '../modals/bdm-nsw-authenticate/bdm-nsw-authenticate.component';
import { PanelType, SiteService } from './site.service';
import { ScrollLockService } from '../scroll-lock/services/scroll-lock.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Arrangement } from '../classes/arrangement';
import { ImageCropperModalComponent } from '../modals/image-cropper/image-cropper-modal.component';
import {
  FileUploadComponent,
  FileUploadModalData,
} from '../modals/file-upload/file-upload.component';
import {
  MediaViewerComponent,
  MediaViewerModalData,
} from '../modals/media-viewer/media-viewer.component';
import { SavePromptComponent } from '../modals/save-prompt/save-prompt.component';
import {
  FULLSCREEN_DIALOG_CLASS,
  ReleaseNotesComponent,
  ReleaseNotesModalData,
} from '../modals/release-notes/release-notes.component';
import { InputTextInputComponent } from '../modals/input-text-input/input-text-input.component';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { BdmVicAuthenticateComponent } from '../modals/bdm-vic-authenticate/bdm-vic-authenticate.component';
import { AccountFirstCallSource } from '../modals/account-firstcall-source/account-firstcall-source.component';
import { AccountFirstCallSourceGroupListComponent } from '../modals/account-firstcall-sourcegroup-list/account-firstcall-sourcegroup-list.component';
import { CreateTransferComponent } from '../modals/create-transfer/create-transfer.component';
import { CreateEventComponent } from '../modals/create-event/create-event.component';
import { MortuaryPreparationOptionsComponent } from '../modals/mortuary-preparation-options/mortuary-preparation-options.component';
import { ShowPdfComponent } from '../modals/show-pdf/show-pdf.component';
import { AccountLocality } from '../modals/account-locality/account-locality.component';
import { AccountLocalityGroupListComponent } from '../modals/account-localitygroup-list/account-localitygroup-list.component';
import { AccountArrangementLocalityListComponent } from '../modals/account-arrangement-locality-list/account-arrangement-locality-list.component';
import { ServiceProviderImportPreviewComponent } from '../modals/service-provider-import-preview/service-provider-import-preview.component';

export interface ModalDataButton {
  label: string;
  key: string;
  class: string;
  icon?: string;
  handler?: () => Observable<any>;
  loading?: boolean;
  disabled?: boolean;
}

export interface ModalData {
  baseZIndex?: number;
  autoZIndex?: boolean;
  isLoading?: boolean;
  preventClose?: boolean;
  title: string;
  copy?: string[];
  list?: string[];
  buttons: ModalDataButton[];
  data?: any;
}

export interface SiteContactModalData {
  _meta: { id: string; index: number; deleted?: boolean };
  name: string;
  paths: { path: string; _meta: { id: string; index: number; deleted?: boolean } }[];
  contact: any;
}

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  constructor(
    private dialogService: DialogService,
    private messageService: MessageService,
    private siteService: SiteService,
    private router: Router,
    private scrollLockService: ScrollLockService
  ) {}

  // Every modal routes through here so they share one motion: 350ms panel
  // zoom + mask fade (see the p-component-overlay overrides in styles.scss -
  // the mask element only lives as long as this panel transition, so the two
  // durations must match). A call site passing its own transitionOptions wins.
  //
  // And one scroll lock (#210): the page behind a modal stays put under the
  // wheel, scrollbar and keyboard. bodyLock is depth-counted and shared with
  // SiteService's panels, so each modal gives back exactly the one it took.
  // Every way a dialog closes (close(), Escape, the mask) ends in its leave
  // animation's destroy(), so onDestroy is the single unlock. This is the
  // desktop half only; iPad touch pans are handled by touch-action (see
  // scroll-lock.service.ts), not here.
  private open(component: Type<any>, config: DynamicDialogConfig): DynamicDialogRef {
    const ref = this.dialogService.open(component, {
      transitionOptions: '350ms cubic-bezier(0.25, 1, 0.5, 1)',
      ...config,
    });
    this.scrollLockService.bodyLock();
    ref.onDestroy.pipe(first()).subscribe(() => this.scrollLockService.bodyUnlock());
    return ref;
  }

  showPdf(data: any): DynamicDialogRef {
    return this.open(ShowPdfComponent, {
      header: data.title || 'View PDF',
      styleClass: 'modal modal-generic',
      width: '90vw',
      height: '90vh',
      baseZIndex: 10000,
      data: data,
    });
  }

  error(data: any): DynamicDialogRef {
    let title = 'Conductor Error';

    if (_.has(data, 'error.error.title')) {
      title = data.error.error.title;
    } else if (_.has(data, 'data.error.title')) {
      title = data.error.title;
    } else if (_.has(data, 'data.title')) {
      title = data.title;
    }

    return this.open(ErrorComponent, {
      header: title,
      // width: '70%',
      // contentStyle: { "max-height": "500px", "overflow": "auto" },
      styleClass: 'modal modal-error',
      baseZIndex: 10000,
      data: data,
    });
  }

  generic(data: ModalData): DynamicDialogRef {
    const options: any = {
      header: data?.title,
      autoZIndex: data.autoZIndex !== undefined ? data.autoZIndex : true,
      baseZIndex: data.baseZIndex !== undefined ? data.baseZIndex : 10000,
      // width: '70%',
      // contentStyle: { "max-height": "500px", "overflow": "auto" },
      styleClass: 'modal modal-generic',
      data: data,
    };

    if (data.preventClose) {
      options.closable = false;
      options.closeOnEscape = false;
    }

    return this.open(GenericComponent, options);
  }

  // Generic upload-only modal. Returns a DynamicDialogRef whose onClose emits
  // { files: FileModel[] } on success, or undefined if cancelled. The caller subscribes
  // to onClose and persists the result however it needs (service media, arrangement doc, …).
  fileUpload(data: FileUploadModalData): DynamicDialogRef {
    return this.open(FileUploadComponent, {
      header: data.title ?? 'Upload files',
      styleClass: 'modal modal-generic',
      baseZIndex: 10000,
      data,
    });
  }

  // Release notes as a slideshow. onClose emits 'update' (reloading) or 'close'.
  releaseNotes(data: ReleaseNotesModalData): DynamicDialogRef {
    const fullscreen = data.fullscreen === true;

    return this.dialogService.open(ReleaseNotesComponent, {
      header: data.release?.title || `Conductor ${data.release?.version ?? ''}`,
      styleClass: `modal modal-generic modal-release-notes${fullscreen ? ' ' + FULLSCREEN_DIALOG_CLASS : ''}`,
      width: fullscreen ? '100vw' : 'min(46rem, 95vw)',
      baseZIndex: 10000,
      data,
    });
  }

  // Viewer for documents (download list) or images (gallery). Read-only.
  mediaViewer(data: MediaViewerModalData): DynamicDialogRef {
    return this.open(MediaViewerComponent, {
      header: data.title ?? (data.kind === 'image' ? 'Images' : 'Documents'),
      styleClass: 'modal modal-generic',
      baseZIndex: 10000,
      data,
    });
  }

  imageCropper(imageBase64: string): DynamicDialogRef {
    return this.open(ImageCropperModalComponent, {
      header: 'Avatar Image',
      styleClass: 'modal modal-generic',
      baseZIndex: 10000,
      data: {
        imageBase64,
      },
    });
  }

  bdmNswAuthenticate(data: any): DynamicDialogRef {
    return this.open(BdmNswAuthenticateComponent, {
      header: 'BDM NSW Authenticate',
      styleClass: 'modal modal-generic',
      baseZIndex: 10000,
      data: data,
    });
  }

  bdmVicAuthenticate(data: any): DynamicDialogRef {
    return this.open(BdmVicAuthenticateComponent, {
      header: 'BDM VIC Authenticate',
      styleClass: 'modal modal-generic',
      baseZIndex: 10000,
      data: data,
    });
  }

  bdmQldAuthenticate(data: any): DynamicDialogRef {
    return this.open(BdmNswAuthenticateComponent, {
      header: 'BDM QLD Authenticate',
      styleClass: 'modal modal-generic',
      baseZIndex: 10000,
      data: data,
    });
  }

  mortuaryPreparationOptions(data: any): DynamicDialogRef {
    return this.open(MortuaryPreparationOptionsComponent, {
      header: 'Mortuary Preparation Options',
      styleClass: 'modal modal-generic',
      baseZIndex: 10000,
      data: data,
    });
  }

  addArrangementSource(data?: any): DynamicDialogRef {
    return this.open(InputNameValuePairComponent, {
      header: 'Add Arrangement Source',
      styleClass: 'modal',
      baseZIndex: 10000,
      data: data,
    });
  }

  listArrangementSource(data?: any): DynamicDialogRef {
    return this.open(AccountArrangementSourceListComponent, {
      header: 'List Arrangement Source',
      styleClass: 'modal',
      baseZIndex: 10000,
      data: data,
    });
  }

  listArrangementLocality(data?: any): DynamicDialogRef {
    return this.open(AccountArrangementLocalityListComponent, {
      header: 'List Arrangement Locality',
      styleClass: 'modal',
      baseZIndex: 10000,
      data: data,
    });
  }

  listReligion(data?: any): DynamicDialogRef {
    return this.open(AccountReligionListComponent, {
      header: 'List Religion',
      styleClass: 'modal',
      baseZIndex: 10000,
      data: data,
    });
  }

  listEventType(data?: any): DynamicDialogRef {
    return this.open(AccountEventTypeListComponent, {
      header: 'List Event Type',
      styleClass: 'modal',
      baseZIndex: 10000,
      data: data,
    });
  }

  listCharities(data?: any): DynamicDialogRef {
    return this.open(AccountCharityListComponent, {
      header: 'List Charities',
      styleClass: 'modal',
      baseZIndex: 10000,
      data: data,
    });
  }

  listFuneralCosts(data?: any): DynamicDialogRef {
    return this.open(AccountFuneralCostListComponent, {
      header: 'Funeral Costs',
      styleClass: 'modal',
      width: '90vw',
      baseZIndex: 10000,
      data: data,
    });
  }

  listFaqs(data?: any): DynamicDialogRef {
    return this.open(AccountFaqListComponent, {
      header: 'Website FAQs',
      styleClass: 'modal',
      width: '80vw',
      baseZIndex: 10000,
      data: data,
    });
  }

  listRegions(data?: any): DynamicDialogRef {
    return this.open(ContentRegionListComponent, {
      header: 'Regions',
      styleClass: 'modal',
      width: '90vw',
      baseZIndex: 10000,
      data: data,
    });
  }

  /**
   * Suburbs (#256): the map, the list and the side panel for one suburb, its data, notes and page. Wide, no padding
   * (the map fills it), and Esc doesn't close it: Esc cancels a lasso or closes the side panel. data.focus = a
   * salCode to open on.
   */
  listSuburbs(data?: { focus?: string }): DynamicDialogRef {
    return this.open(ContentSuburbWorkspaceComponent, {
      header: 'Suburbs',
      styleClass: 'modal modal-wide suburb-workspace-modal',
      width: '96vw',
      contentStyle: { padding: '0', overflow: 'hidden' },
      closeOnEscape: false,
      baseZIndex: 10000,
      data: data,
    });
  }

  /** Find a venue (#250): the arrangers' venue map. From the nav, or from an event (its suburb and purpose). */
  findVenue(data?: VenueMapData): DynamicDialogRef {
    return this.open(VenueMapComponent, {
      header: 'Find a venue',
      styleClass: 'modal modal-wide venue-map-modal',
      width: '96vw',
      contentStyle: { padding: '0', overflow: 'hidden' },
      baseZIndex: 10000,
      data: data ?? {},
    });
  }

    listCharityPartners(data?: any): DynamicDialogRef {
    return this.open(ContentCharityListComponent, {
      header: 'Charity partners',
      styleClass: 'modal',
      width: '90vw',
      baseZIndex: 10000,
      data: data,
    });
  }

  listBlogArticles(data?: any): DynamicDialogRef {
    return this.open(ContentBlogListComponent, {
      header: 'Blog articles',
      styleClass: 'modal',
      width: '90vw',
      baseZIndex: 10000,
      data: data,
    });
  }

  listCmsPages(data?: any): DynamicDialogRef {
    return this.open(ContentPageListComponent, {
      header: 'Website pages',
      styleClass: 'modal',
      width: '90vw',
      baseZIndex: 10000,
      data: data,
    });
  }

  listCmsMenus(data?: any): DynamicDialogRef {
    return this.open(ContentMenuListComponent, {
      header: 'Navigation menus',
      styleClass: 'modal',
      width: '60vw',
      baseZIndex: 10000,
      data: data,
    });
  }

  listSiteServices(data?: any): DynamicDialogRef {
    return this.open(ContentSiteServiceListComponent, {
      header: 'Website services',
      styleClass: 'modal',
      width: '70vw',
      baseZIndex: 10000,
      data: data,
    });
  }

  /** Website → Profiles (#184): the public bios shown on picaluna.com. */
  listProfiles(data?: any): DynamicDialogRef {
    return this.open(ContentProfileListComponent, {
      header: 'Profiles',
      // modal-wide, because plain `.modal` caps at 768px and silently wins
      // over `width` — seven columns, two of them chip lists, need the room.
      styleClass: 'modal modal-wide',
      width: '90vw',
      baseZIndex: 10000,
      data: data,
    });
  }

  editProfile(data?: any): DynamicDialogRef {
    return this.open(ContentProfileComponent, {
      // Esc would close the editor and silently discard unsaved changes.
      closeOnEscape: false,
      header: 'Edit profile',
      styleClass: 'modal modal-wide',
      width: '60vw',
      baseZIndex: 10000,
      data: data,
    });
  }

  /** Editor help (#241) — replaces the generic markdownguide.org link. */
  markdownHelp(): DynamicDialogRef {
    return this.open(MarkdownHelpComponent, {
      header: 'Formatting and live values',
      styleClass: 'modal',
      width: '46rem',
      baseZIndex: 11000,
    });
  }

  listTemplateCopy(data?: any): DynamicDialogRef {
    return this.open(ContentTemplateCopyListComponent, {
      header: 'Template copy',
      styleClass: 'modal',
      width: '70vw',
      baseZIndex: 10000,
      data: data,
    });
  }

  addAccountContact(data?: SiteContactModalData): DynamicDialogRef {
    return this.open(AccountContactComponent, {
      header: 'Add Account Contact',
      styleClass: 'modal overflow-visible',
      baseZIndex: 10000,
      data: data,
    });
  }

  listAccountContact(data: any): DynamicDialogRef {
    return this.open(AccountContactListComponent, {
      header: 'Edit Account Contact',
      styleClass: 'modal modal-contact-list',
      baseZIndex: 10000,
      data: data,
    });
  }

  compiler(data?: any): DynamicDialogRef {
    return this.open(CompilerComponent, {
      header: 'Compiler Example',
      styleClass: 'modal',
      baseZIndex: 10000,
      data: data,
    });
  }

  inputTextArea(data?: ModalData): DynamicDialogRef {
    return this.open(InputTextAreaComponent, {
      header: data?.title,
      styleClass: 'modal modal-input',
      baseZIndex: 10000,
      data: data,
    });
  }

  closeInvoiceConfirmation(data?: Partial<ModalData>): DynamicDialogRef {
    return this.open(CloseInvoiceConfirmationComponent, {
      header: 'Close Invoice Confirmation',
      styleClass: 'modal modal-input',
      baseZIndex: 10000,
      data: data,
    });
  }

  inputTextInput(data?: ModalData): DynamicDialogRef {
    return this.open(InputTextInputComponent, {
      header: data?.title,
      styleClass: 'modal modal-input',
      baseZIndex: 10000,
      data: data,
    });
  }

  inputNameValuePair(data?: any): DynamicDialogRef {
    return this.open(InputNameValuePairComponent, {
      header: data?.title,
      styleClass: 'modal modal-input',
      baseZIndex: 11000,
      data: data,
    });
  }

  addAccountHospitalOrNursingHome(data?: any): DynamicDialogRef {
    return this.open(AccountHospitalAndNursingHomeComponent, {
      header: data?.title,
      styleClass: 'modal modal-input',
      baseZIndex: 11000,
      data: data,
    });
  }

  listAccountHospitalOrNursingHome(): DynamicDialogRef {
    return this.open(AccountHospitalAndNursingHomeListComponent, {
      header: 'Edit existing Hospitals / Nursing Homes',
      styleClass: 'modal modal-input',
      baseZIndex: 11000,
      data: null,
    });
  }

  inputSignature(data?: ModalData): DynamicDialogRef {
    return this.open(InputSignatureComponent, {
      header: data?.title,
      styleClass: 'modal modal-signature',
      baseZIndex: 11000,
      data: data,
      closable: false,
      closeOnEscape: false,
    });
  }

  activateAccount(): DynamicDialogRef {
    return this.open(ActivateAccountModalComponent, {
      header: 'Activate Account',
      styleClass: 'modal',
      baseZIndex: 11000,
      closable: false,
    });
  }

  existingArrangementSaved(showNextButton: boolean = true): DynamicDialogRef {
    const options = {
      title: 'Arrangement Updated Successfully',
      copy: [`You successfully updated the arrangement.`],
      buttons: [{ label: 'Stay on this Page', key: 'stay', class: 'p-button-secondary' }],
    };

    if (showNextButton) {
      options.buttons.push({
        label: 'Go to Next Page',
        key: 'next-page',
        class: 'p-button-primary',
      });
    }

    const modal = this.generic(options);

    return modal;
  }

  newArrangementSaved(arrangement: Arrangement): void {
    const modal = this.generic({
      title: 'Arrangement Created Successfully',
      copy: [
        `You successfully created a new arrangement!`,
        `Do you want to continue filling out the arrangement? Or do you want to view other existing arrangements?`,
      ],
      buttons: [
        {
          label: 'View other existing arrangements',
          key: 'view-existing',
          class: 'p-button-secondary',
        },
        { label: 'Continue filling out the arrangement', key: 'continue', class: '' },
      ],
    });

    modal.onClose.pipe(first()).subscribe({
      next: (key: string) => {
        if (key === 'view-existing') {
          this.router.navigate(['dashboard', 'arrangements', 'list']);
        } else {
          // 200ms, not 500: the delay exists only so the slide-in starts after the
        // panel DOM settles - overview.component's PANEL_OPEN_DELAY_MS proves
        // 200ms suffices, and the extra 300ms was pure perceived latency on the
        // hottest action in the app (2026-09-04 audit).
        const timeoutDuration = 200;

          this.siteService.openPanel(PanelType.ArrangementDetail, {
            timeoutDuration: timeoutDuration,
            arrangement: arrangement,
          });

          setTimeout(() => {
            this.router.navigate(['dashboard', 'arrangements', 'list']);
          }, timeoutDuration);
        }
      },
    });
  }

  savePromptOnNavigate(arrangement: Arrangement, compare: any): DynamicDialogRef {
    return this.open(SavePromptComponent, {
      header: 'Save Changes?',
      styleClass: 'modal modal-generic',
      baseZIndex: 10000,
      data: { arrangement, type: 'navigate', compare },
    });
  }

  savePromptOnClose(arrangement: Arrangement, compare: any): DynamicDialogRef {
    return this.open(SavePromptComponent, {
      header: 'Save Changes?',
      styleClass: 'modal modal-generic',
      baseZIndex: 10000,
      data: { arrangement, type: 'close', compare },
    });
  }

  addAccountFirstCallSource(data?: any): DynamicDialogRef {
    return this.open(AccountFirstCallSource, {
      header: data?.title,
      styleClass: 'modal modal-input',
      baseZIndex: 11000,
      data: data,
    });
  }

  addAccountLocality(data?: any): DynamicDialogRef {
    return this.open(AccountLocality, {
      header: data?.title,
      styleClass: 'modal modal-input',
      baseZIndex: 11000,
      data: data,
    });
  }

  listAccountFirstCallSourceGroup(data?: any): DynamicDialogRef {
    return this.open(AccountFirstCallSourceGroupListComponent, {
      header: 'List Arrangement Source Group',
      styleClass: 'modal',
      baseZIndex: 10000,
      data: data,
    });
  }

  createTransfer(data: any): DynamicDialogRef {
    return this.open(CreateTransferComponent, {
      header: 'Create New Transfer',
      styleClass: 'modal',
      baseZIndex: 10000,
      data: data,
    });
  }

  listAccountLocalityGroup(data?: any): DynamicDialogRef {
    return this.open(AccountLocalityGroupListComponent, {
      header: 'List Arrangement Locality Group',
      styleClass: 'modal',
      baseZIndex: 10000,
      data: data,
    });
  }

  createEvent(data: any): DynamicDialogRef {
    return this.open(CreateEventComponent, {
      header: 'Create New Event',
      styleClass: 'modal',
      baseZIndex: 10000,
      data: data,
    });
  }

  serviceProviderImportPreview(data: any): DynamicDialogRef {
    return this.open(ServiceProviderImportPreviewComponent, {
      header: 'Review Import Changes',
      styleClass: 'modal modal-generic',
      baseZIndex: 11000,
      data: data,
    });
  }
}
